/** Records a B2C redirect error so the login page can show it instead of the app silently
 *  bouncing the user into another sign-in round-trip (see initializeMsal in app.config.ts, which
 *  previously swallowed every handleRedirectPromise() error with just a console.log -- including
 *  AADB2C90118, the code B2C returns when the user clicks "Forgot your password?" on the sign-in
 *  page. Guards then saw no active account and called loginRedirect() again, restarting the SAME
 *  policy from scratch with no explanation -- from the user's perspective, clicking reset "does
 *  nothing" and just reloads the sign-in form (found live in production 2026-07-13).
 *
 *  sessionStorage (not a plain module variable) so this survives the guard's own navigation to
 *  /login after the app re-bootstraps on B2C's redirect back to index.html -- same convention as
 *  ExtranetService's activeRole preference. Per-tab, cleared on tab close or explicit retry. */
const KEY = 'ext.b2cRedirectError';

export function storeB2cRedirectError(error: unknown): void {
  const message = error instanceof Error ? error.message : String(error);
  try {
    sessionStorage.setItem(KEY, message);
  } catch {
    // sessionStorage unavailable (private browsing, etc.) -- the error is still console.error'd
    // by the caller, just won't survive to the login page.
  }
}

export function getB2cRedirectError(): string | null {
  try {
    return sessionStorage.getItem(KEY);
  } catch {
    return null;
  }
}

export function clearB2cRedirectError(): void {
  try {
    sessionStorage.removeItem(KEY);
  } catch {
    // ignore
  }
}

/** True for AADB2C90118, the specific code B2C returns when the user clicks "Forgot your
 *  password?" instead of signing in -- MSAL surfaces it as a redirect error rather than a normal
 *  result. Distinguishing it lets the login page show a more accurate message than a generic
 *  sign-in failure. */
export function isForgotPasswordError(message: string | null): boolean {
  return !!message && message.includes('AADB2C90118');
}
